/**
 * Functions that helps the complicated Object type calculation
 *
 * @packageDocumentation
 */

/**
 * Deep merge two object recursively and return the resulting object
 *
 * @template {T extends Record<string, unknown>}
 * @param {T} obj1
 * @param {T} obj2
 * @return {T} object, object containing properties from obj1 and obj2
 */
export const deepMerge = <
    T extends Record<string, unknown> = Record<string, unknown>
>(
    obj1: T,
    obj2: T
): T => {
    if (!obj1) return obj2
    if (!obj2) return obj1
    const tempObj2 = JSON.parse(JSON.stringify(obj2))
    for (const key of Object.keys(tempObj2)) {
        if (tempObj2[key] instanceof Object)
            Object.assign(
                tempObj2[key],
                deepMerge(
                    key in obj1 ? (obj1[key] as Record<string, unknown>) : {},
                    tempObj2[key]
                )
            )
    }
    return { ...obj1, ...tempObj2 }
}

/**
 * Converts key/value pairs object to key/value string
 *
 * @template {T}
 * @param {T} param - ex. {no_pol:true, page:index }
 * @param {string} delim
 * @param {string} divider
 * @returns {string}
 */
export const objectToString = <T = Record<string, string>>(
    param: T,
    delim = '=',
    divider = '&'
): string => {
    const object = param as unknown as Record<string, string>
    return Object.keys(object)
        .map((key) => `${key}${delim}${object[key]}`)
        .join(`${divider}`)
}
